// Canonical share-link + native-share helpers (iter: Android share fix).
// Inside the packaged Android app window.location.origin is "https://localhost"
// (Capacitor WebView), so share links must be rebuilt on the public domain.
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

const PROD_WEB_ORIGIN = 'https://wassla.tech';

const isInternalOrigin = (origin) =>
  !origin ||
  /^(capacitor|ionic|file):/i.test(origin) ||
  /\/\/(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.)/i.test(origin);

export function getPublicOrigin() {
  const winOrigin =
    (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  if (Capacitor.isNativePlatform() || isInternalOrigin(winOrigin)) {
    const envOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (envOrigin && !isInternalOrigin(envOrigin)) return envOrigin;
    return PROD_WEB_ORIGIN;
  }
  return winOrigin.replace(/\/+$/, '');
}

export function buildShareUrl(hashPath) {
  const p = String(hashPath || '').startsWith('/') ? hashPath : `/${hashPath || ''}`;
  return `${getPublicOrigin()}/#${p}`;
}

// Opens the Android/iOS native share sheet via the Capacitor Share plugin,
// falls back to the Web Share API on browsers. Returns { completed, method }:
// completed=false + method='none' means the caller should run its own
// copy-link fallback. A user cancel counts as completed (silent no-op).
export async function shareNative({ title, text, url }) {
  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({ title, text, url, dialogTitle: title });
      return { completed: true, method: 'native' };
    } catch (e) {
      const msg = String(e?.message || e?.errorMessage || '').toLowerCase();
      if (msg.includes('cancel')) return { completed: true, method: 'canceled' };
      // Plugin failure — fall through to web share / copy fallback.
    }
  }
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { completed: true, method: 'web' };
    } catch (e) {
      if (e?.name === 'AbortError') return { completed: true, method: 'canceled' };
    }
  }
  return { completed: false, method: 'none' };
}

export function canNativeShare() {
  return (
    Capacitor.isNativePlatform() ||
    (typeof navigator !== 'undefined' && !!navigator.share)
  );
}

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) { /* fall through */ }
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
  ta.setAttribute('readonly', '');
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) throw new Error('copy-failed');
  return true;
}
